/**
 * 通貨まわりの共通ユーティリティ (functions / admin-web 共用)。
 *
 * メーカー取引通貨 (ProductCategory.manufacturerCurrency) を単一の情報源として、
 * 発注カード・送金依頼メール・送金依頼リスト・送金完了メールの金額表示を統一する。
 */

/** 対応通貨コード (ISO 4217)。必要に応じて追加可能。 */
export const SUPPORTED_CURRENCIES = [
  'EUR',
  'USD',
  'JPY',
  'GBP',
  'CHF',
  'AUD',
  'CAD',
  'CNY',
  'KRW',
  'SGD',
  'HKD',
] as const;

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

/** 通貨コード → 記号。未知コードはコード自体を接頭辞として返す。 */
const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
  USD: '$',
  JPY: '¥',
  GBP: '£',
  CHF: 'CHF ',
  AUD: 'A$',
  CAD: 'C$',
  CNY: '¥',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
};

/** 小数を持たない通貨 (整数表示にする)。 */
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW']);

/** メーカー取引通貨の既定値。旧データ (未設定) は EUR 扱い (従来挙動を踏襲)。 */
export const DEFAULT_MANUFACTURER_CURRENCY = 'EUR';

/** 通貨コードを正規化 (大文字化・未知は既定値へ)。 */
export function normalizeCurrency(code: string | null | undefined): string {
  const c = (code ?? '').trim().toUpperCase();
  return c || DEFAULT_MANUFACTURER_CURRENCY;
}

/**
 * 金額を通貨の桁数で丸める (2026-10-26 追加)。
 * JPY / KRW は整数、それ以外 (EUR 等) は小数 2 桁 (セント単位)。
 * 例: roundMoney(579.15, 'EUR') → 579.15 / roundMoney(1234.5, 'JPY') → 1235
 */
export function roundMoney(amount: number, code: string | null | undefined): number {
  const c = normalizeCurrency(code);
  const factor = ZERO_DECIMAL_CURRENCIES.has(c) ? 1 : 100;
  return Math.round(amount * factor) / factor;
}

/** 通貨記号を返す (例: 'EUR' → '€')。 */
export function currencySymbol(code: string | null | undefined): string {
  const c = normalizeCurrency(code);
  return CURRENCY_SYMBOL[c] ?? `${c} `;
}

/**
 * 金額を通貨付きで整形する。
 * 例: formatMoney(3200, 'EUR') → "€3,200" / formatMoney(100000, 'JPY') → "¥100,000"
 * JPY/KRW は整数、その他は小数 2 桁まで保持 (末尾ゼロは付けない)。
 */
export function formatMoney(
  amount: number | null | undefined,
  code: string | null | undefined,
): string {
  const c = normalizeCurrency(code);
  const n = typeof amount === 'number' && isFinite(amount) ? amount : 0;
  const maximumFractionDigits = ZERO_DECIMAL_CURRENCIES.has(c) ? 0 : 2;
  const num = n.toLocaleString('ja-JP', { maximumFractionDigits });
  return `${currencySymbol(c)}${num}`;
}
